/* eslint-disable local/no-block-disable-of-nested-eslint */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
import { ComponentType } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ChevronRightLargeIcon from "@atlaskit/icon/glyph/chevron-right-large";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useHeroState } from "../util/hero-context";

type HeroCardProps = {
    readonly eyebrow: string;
    readonly title: string;
    readonly image: string;
    readonly BackgroundImage: ComponentType;
    readonly idx: number;
    readonly label: string;
    readonly showUpdatedHeroExperiment?: boolean;
};

const CardStyles = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textAlign: "left",
    padding: "0",
    width: "260px",
    height: "496px",
    minWidth: "260px",
    minHeight: "496px",
    border: "0",
    borderRadius: "22px",
    background: "var(--Primary-White, #FFF)",
    boxShadow:
        "0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)",
    cursor: "pointer",
    transition: "300ms",
    overflow: "clip",
    [media.above.lg]: {
        ["&:hover"]: {
            boxShadow: token("elevation.shadow.overlay"),
            transform: "translateY(-10px)",
            ["svg"]: {
                opacity: "1"
            },
            [".chevron"]: {
                opacity: "1",
                transform: "translateX(10px)"
            }
        }
    },
    [media.only.md]: {
        width: "261px",
        minWidth: "216px",
        height: "400px",
        minHeight: "400px"
    },
    [media.only.sm]: {
        width: "261px",
        minWidth: "216px",
        height: "400px",
        minHeight: "400px"
    },
    [media.below.sm]: {
        height: "320px",
        minHeight: "320px",
        width: "261px",
        minWidth: "216px"
    }
});

const textStyles = css({
    paddingInlineStart: "34px"
});

const eyebrowStyles = css({
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "16px",
    letterSpacing: "0.24px",
    textTransform: "uppercase",
    paddingBlockStart: "29px",
    textWrap: "nowrap",
    [media.below.sm]: {
        fontSize: "9px",
        lineHeight: "13px",
        width: "142px"
    }
});

const titleStyles = css({
    display: "flex",
    width: "158px",
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "30px",
    paddingBlockStart: "7px",
    wordWrap: "break-word",
    hyphens: "auto",
    justifyContent: "space-between",
    [media.only.md]: {
        width: "142px",
        marginBlockEnd: "24px",
        height: "58px",
        fontSize: "20px"
    },
    [media.only.sm]: {
        width: "142px",
        marginBlockEnd: "24px",
        height: "58px",
        fontSize: "20px"
    },
    [media.below.sm]: {
        width: "142px",
        marginBlockEnd: "24px",
        height: "58px",
        color: "#000",
        fontSize: "20px",
        fontWeight: "600",
        lineHeight: "28px"
    }
});

const experimentTitleStyles = css({
    display: "flex",
    width: "216px",
    color: "var(--Primary-Black, var(--DarkNeutral-Opaque-DarkNeutral-100, #101214))",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "30px",
    paddingBlockStart: "7px",
    wordWrap: "break-word",
    hyphens: "none",
    justifyContent: "space-between",
    [media.only.md]: {
        width: "190px",
        marginBlockEnd: "24px",
        height: "58px",
        fontSize: "18px"
    },
    [media.only.sm]: {
        width: "190px",
        marginBlockEnd: "24px",
        height: "58px",
        fontSize: "16px"
    },
    [media.below.sm]: {
        width: "190px",
        marginBlockEnd: "24px",
        height: "58px",
        color: "#000",
        fontSize: "16px",
        fontWeight: "600",
        lineHeight: "20px"
    }
});

const chevronStyles = css({
    transition: "transform 0.3s ease, opacity 0.3s ease",
    width: "48px",
    height: "48px",
    opacity: "0",
    alignSelf: "flex-end",
    [media.only.md]: {
        opacity: "1",
        alignSelf: "flex-start",
        position: "relative"
    },
    [media.below.md]: {
        opacity: "1",
        alignSelf: "center",
        position: "relative"
    }
});

const imageContainer = css({
    position: "relative",
    overflowY: "clip",
    maxHeight: "400px"
});

const imageStyles = css({
    height: "339px",
    borderRadius: "22px",
    marginInlineStart: "34px",
    position: "absolute",
    insetBlockEnd: "0",
    [media.below.lg]: {
        insetBlockStart: "0"
    }
});

export const HeroCard = ({
    eyebrow,
    title,
    image,
    BackgroundImage,
    idx,
    label,
    showUpdatedHeroExperiment = false
}: HeroCardProps) => {
    const { openModal } = useHeroState();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = () => {
        openModal(idx);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: `atlas-55799-${label}-modal-opened`,
                section: "hero-section"
            }
        }).fire();
    };

    return (
        <button css={CardStyles} onClick={handleClick}>
            <div css={textStyles}>
                <div css={eyebrowStyles}>{eyebrow}</div>
                <div
                    css={[
                        showUpdatedHeroExperiment && experimentTitleStyles,
                        !showUpdatedHeroExperiment && titleStyles
                    ]}
                >
                    {title}
                    {showUpdatedHeroExperiment && (
                        <span css={chevronStyles} className="chevron">
                            <ChevronRightLargeIcon label="chevron" />
                        </span>
                    )}
                </div>
            </div>
            <div css={imageContainer}>
                <BackgroundImage />
                <div css={imageStyles}>
                    <img src={image} width={227} alt="" />
                </div>
            </div>
        </button>
    );
};
